import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { FeedbackBuilder, firstDefined, useFeedbackService } from '@gafe/common-ui';
import merge from 'classnames';

import { AssistingBanner } from '@/components/generic/AssistingBanner';
import { Button } from '@/components/generic/Button';
import { IconLogout } from '@/components/icons/IconLogout';
import { Header } from '@/components/layout/Header/Header';
import { Modal } from '@/components/proxy/reactstrap/Modal';
import { AbilityContext } from '@/hooks/ability/Can';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { ROUTES } from '@/router';
import { Axios } from '@/services/Axios';

import { getNavLinkConfig } from './constants';
import { Props } from './types';

import styles from './SideBar.module.scss';

export const SideBar = (props: Props) => {
  const history = useHistory();
  const ability = useAbility(AbilityContext);
  const { userInfo } = useAuth();
  const mediaType = useDeviceDetection();
  const feedbackService = useFeedbackService();

  const allowedToViewSalesResources =
    userInfo?.companyInfo.isTimberlineSolarEnabled ||
    userInfo?.companyInfo.isTimberlineSolarPilotEnabled ||
    false;

  useEffect(() => {
    setTimeout(() => {
      const sideBar = document.getElementsByClassName(styles.modal)[0];

      if (sideBar) {
        const assistingBanner = document.getElementById(AssistingBanner.containerId);
        const header = document.getElementById(Header.containerId);
        const top = (assistingBanner?.offsetHeight || 0) + (header?.offsetHeight || 0);
        const modal = sideBar.getElementsByClassName('modal')[0] as HTMLElement;
        const modalBackdrop = sideBar.getElementsByClassName('modal-backdrop')[0] as HTMLElement;

        modal.style.paddingTop = `${top}px`;
        modalBackdrop.style.marginTop = `${top}px`;
      }
    }, 0);
  }, [props.isOpened, mediaType]);

  const onItemClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    props.toggle();
  };

  const navigateToRoute = (targetRoute: string) => (event: React.MouseEvent<HTMLElement>) => {
    onItemClick(event);
    history.push(targetRoute);
  };

  const openExternal = (externalLink: string) => async (event: React.MouseEvent<HTMLElement>) => {
    onItemClick(event);
    let link: string | undefined = externalLink;

    if (externalLink === 'workramp') {
      try {
        const { data } = await Axios.post('/training/instantAuth');

        link = firstDefined('#', data?.url);
      } catch {
        link = undefined;
        const feedback = new FeedbackBuilder()
          .error()
          .message('There was an error opening the link.')
          .title('Error')
          .build();

        feedbackService.show(feedback);
      }
    }

    if (link) {
      window.open(link, '_blank');
    }
  };

  const logoutUser = (event: React.MouseEvent<HTMLElement>) => {
    onItemClick(event);
    history.push(ROUTES.Auth.Logout);
  };

  const menuList = getNavLinkConfig({
    allowedToViewSalesResources,
    role: userInfo?.role ?? 0,
    allowedToViewWorkRamp: ability.can('view', 'WORKRAMP'),
    companyPricing:
      ability.can('view', 'SKU_PRICING') &&
      userInfo?.companyInfo.distributionAgreementSignedDate !== null,
    newManageUsers: ability.can('view', 'NEW_MANAGE_USERS'),
  });

  return (
    <>
      <button
        className={merge(styles.hamburger, { [styles.active]: props.isOpened })}
        data-testid="hamburger-button"
        onClick={props.toggle}
        type="button"
      >
        <span />
        <span />
      </button>

      <Modal
        isOpen={props.isOpened}
        toggle={props.toggle}
        wrapClassName={styles.modal}
        zIndex={1300}
      >
        <div>
          {menuList.map(({ title, target, external, hidden }) => (
            <a
              className={merge(styles.link, { [styles.hidden]: hidden })}
              href={target}
              key={title}
              onClick={external ? openExternal(target) : navigateToRoute(target)}
            >
              {title}
            </a>
          ))}
        </div>

        <Button
          className={styles.logout_button}
          color="transparent"
          colorSchema="outlined"
          onClick={logoutUser}
        >
          Log out
          <IconLogout className={styles.logout_icon} />
        </Button>
      </Modal>
    </>
  );
};
